import React, { useEffect, useState } from "react";
import type { CategoryNode } from "@/types/pim";
import { Input } from "antd";

import AttributeFilters from "./InventoryList.AttributeFilters";
import SearchBar from "./InventoryHome.SearchBar";
import DisableWhileNavigating from "./DisableWhileNavigating";
import { useCatalogResults } from "./InventoryList.ResultsContext";

const { Search } = Input;

interface SidebarProps {
  activeFilterCount: number;
  clearAllFilters: () => void;
  selectedCategory?: CategoryNode | null;
  attributeFilters: Record<string, any>;
  handleAttributeFilterChange: (attributeName: string, value: any) => void;
  searchQuery?: string;
  onSearch: (value: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeFilterCount,
  clearAllFilters,
  selectedCategory,
  attributeFilters,
  handleAttributeFilterChange,
  searchQuery,
}) => {
  // Result counts come from the streamed grid via context (null until it mounts).
  const { loadedCount, total, pageSize, currentPage } = useCatalogResults();
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const [isComponentReady, setIsComponentReady] = useState(false);

  // Set component as ready after initial load and a brief delay for layout settling
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsComponentReady(true);
    }, 300); // 300ms delay to allow layout to settle

    return () => clearTimeout(timer);
  }, []);

  // Handle sidebar resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      // Constrain width between 280px and 600px
      if (newWidth >= 280 && newWidth <= 600) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return (
    <div
      className="hidden relative lg:flex lg:flex-col overflow-y-auto bg-white min-h-full"
      style={{ width: selectedCategory ? `${sidebarWidth}px` : '100%' }}
    >
      {/* Sidebar with search and filter button */}
      <div className="bg-white">
        <div className="px-4 py-3 min-h-26.5">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]" style={{ maxWidth: selectedCategory ? '100%' : '450px' }}>
              <SearchBar />
            </div>
          </div>

          {/* Results count — waits for the streamed grid to publish its counts */}
          {
            selectedCategory !== null && total !== null && loadedCount !== null ? (
              <div className="mt-2 text-sm text-gray-600 wrap-break-word">
                {
                  currentPage && pageSize ? (
                    <div>{`Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, total)} of ${total.toLocaleString()} results`}</div>
                  ) : (
                    <>{`Showing ${loadedCount.toLocaleString()} of ${total.toLocaleString()} results`}</>
                  )
                }
                <span className="ml-2 text-gcopper font-medium">
                  ({activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} active)
                </span>
              </div>
            ) : null
          }
        </div>
      </div>

      {
        selectedCategory && (
          <div className="px-4 overflow-x-hidden flex-1">
            <DisableWhileNavigating>
              <div className="flex items-center mb-6 min-h-8">
                <button
                  disabled={activeFilterCount === 0}
                  onClick={clearAllFilters}
                  className={`${activeFilterCount === 0 ? "hover:cursor-not-allowed border-gray-200 text-gray-300" : "hover:cursor-pointer border-gray-300 text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"} text-sm border px-4 py-1.25 rounded-xl`}
                >
                  Clear all filters
                </button>
              </div>

              {/* Attribute filters */}
              {selectedCategory && (
                <div className="mb-6">
                  <AttributeFilters
                    searchQuery={searchQuery}
                    category={selectedCategory || null}
                    filters={attributeFilters}
                    onFilterChange={handleAttributeFilterChange}
                  />
                </div>
              )}
            </DisableWhileNavigating>

            {!selectedCategory && (
              <div className="text-sm text-gray-500 italic px-3">
                Select a category to filter by attributes
              </div>
            )}
          </div>
        )
      }

      {/* Resize handle */}
      {isComponentReady && selectedCategory && (
        <div
          className={`absolute top-0 right-0 bottom-0 w-0.5 cursor-col-resize transition-colors group ${isResizing ? 'bg-gcopper' : 'hover:bg-gcopper'}`}
          onMouseDown={handleMouseDown}
        >
          <div className={`absolute top-64 right-0 -translate-y-1/2 w-3 h-12 rounded-l-md transition-colors flex items-center justify-center ${isResizing ? 'bg-gcopper' : 'bg-gray-400 group-hover:bg-gcopper'}`}>
            <svg className="w-2 h-4 text-white" fill="currentColor" viewBox="0 0 6 16">
              <circle cx="1" cy="2" r="1" />
              <circle cx="1" cy="8" r="1" />
              <circle cx="1" cy="14" r="1" />
              <circle cx="5" cy="2" r="1" />
              <circle cx="5" cy="8" r="1" />
              <circle cx="5" cy="14" r="1" />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
};

export default Sidebar;